/**
 * Admin → Utilisateurs tab
 *
 * Admin-only view that lists admin_accounts and lets the owner create /
 * update / delete secretary (and other admin) accounts.
 *
 * Backend endpoints:
 *   GET    /api/admin/users             — list
 *   POST   /api/admin/users             — create { username, password, role, active }
 *   PUT    /api/admin/users/{id}        — update { password?, role?, active? }
 *   DELETE /api/admin/users/{id}        — delete
 *   PUT    /api/admin/users/me          — self-service password change
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, X, Users, Loader2, Shield, ShieldCheck,
  KeyRound, Pencil, UserCheck, UserX,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  admin: {
    label: "Administrateur",
    cls: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30",
    icon: ShieldCheck,
  },
  secretary: {
    label: "Secrétaire",
    cls: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    icon: Shield,
  },
};

const RolePill = ({ role }) => {
  const meta = ROLE_LABELS[role] || ROLE_LABELS.secretary;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded border ${meta.cls}`}>
      <Icon size={11} />
      {meta.label}
    </span>
  );
};

const EMPTY_FORM = { username: "", password: "", role: "secretary", active: true };

export default function UsersTab({ currentUsername = "admin" }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null | "new" | user.id
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState({});

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/users`);
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur au chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId("new");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const saveNew = async () => {
    const uname = (form.username || "").trim().toLowerCase();
    if (!uname) return toast.error("Nom d'utilisateur requis");
    if (!form.password || form.password.length < 8) return toast.error("Mot de passe : 8 caractères minimum");
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/users`, { ...form, username: uname });
      toast.success(`Utilisateur ${uname} créé`);
      cancelEdit();
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur à la création");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API}/api/admin/users/${u.id}`, { active: !u.active });
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const resetPassword = async (u) => {
    const pwd = passwordDraft[u.id];
    if (!pwd || pwd.length < 8) return toast.error("Mot de passe : 8 caractères minimum");
    try {
      await axios.put(`${API}/api/admin/users/${u.id}`, { password: pwd });
      toast.success(`Mot de passe mis à jour pour ${u.username}`);
      setPasswordDraft((p) => ({ ...p, [u.id]: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const changeRole = async (u, newRole) => {
    try {
      await axios.put(`${API}/api/admin/users/${u.id}`, { role: newRole });
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement l'utilisateur « ${u.username} » ?`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${u.id}`);
      toast.success(`${u.username} supprimé`);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur à la suppression");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black uppercase italic tracking-tighter text-white">
            Utilisateurs <span className="text-[#FF3B30]">.</span>
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Gestion des comptes admin et secrétaire — accès au back-office.
          </p>
        </div>
        {editingId !== "new" && (
          <button
            onClick={startNew}
            data-testid="users-add-btn"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-4 py-2.5 rounded uppercase tracking-wider text-sm"
          >
            <Plus size={16} /> Nouvel utilisateur
          </button>
        )}
      </div>

      {/* New user form */}
      {editingId === "new" && (
        <div className="bg-[#121212] border border-white/[0.06] rounded-lg p-5 space-y-4" data-testid="users-create-form">
          <h3 className="text-white font-bold uppercase text-sm tracking-wider">Nouveau compte</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Nom d'utilisateur</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                data-testid="users-username-input"
                autoCapitalize="none"
                className="w-full bg-[#1C1C1E] border border-white/10 rounded px-3 py-2 text-white focus:outline-none focus:border-white/30"
                placeholder="noha"
              />
            </div>
            <div>
              <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Mot de passe (8 car. min)</label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="users-password-input"
                autoCapitalize="none"
                autoComplete="new-password"
                className="w-full bg-[#1C1C1E] border border-white/10 rounded px-3 py-2 text-white focus:outline-none focus:border-white/30"
                placeholder="MotDePasseSolide2026!"
              />
            </div>
            <div>
              <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Rôle</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="users-role-select"
                className="w-full bg-[#1C1C1E] border border-white/10 rounded px-3 py-2 text-white focus:outline-none focus:border-white/30"
              >
                <option value="secretary">Secrétaire — Planning + Clients uniquement</option>
                <option value="admin">Administrateur — Accès complet</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={saveNew}
              disabled={saving}
              data-testid="users-create-save"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-[#FF3B30]/50 text-white font-bold px-4 py-2 rounded uppercase text-sm tracking-wider"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Créer
            </button>
            <button
              onClick={cancelEdit}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-4 py-2 rounded uppercase text-sm tracking-wider"
            >
              <X size={14} /> Annuler
            </button>
          </div>
        </div>
      )}

      {/* Users list */}
      {loading ? (
        <div className="flex items-center justify-center p-12 text-white/40">
          <Loader2 className="animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-white/40 text-sm text-center p-12">
          Aucun utilisateur enregistré.
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => {
            const isSelf = u.username === currentUsername;
            return (
              <div
                key={u.id}
                data-testid={`user-row-${u.username}`}
                className={`bg-[#121212] border border-white/[0.06] rounded-lg p-4 ${!u.active ? "opacity-50" : ""}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Users size={16} className="text-white/60" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-bold">{u.username}</span>
                        {isSelf && (
                          <span className="text-[10px] uppercase tracking-wider bg-white/5 text-white/60 px-1.5 py-0.5 rounded">
                            vous
                          </span>
                        )}
                        <RolePill role={u.role} />
                        {!u.active && (
                          <span className="text-[10px] uppercase tracking-wider bg-white/5 text-white/40 px-1.5 py-0.5 rounded">
                            désactivé
                          </span>
                        )}
                      </div>
                      {u.last_login_at && (
                        <div className="text-white/40 text-xs mt-0.5">
                          Dernière connexion : {new Date(u.last_login_at).toLocaleString("fr-FR")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!isSelf && (
                      <>
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u, e.target.value)}
                          data-testid={`user-role-${u.username}`}
                          className="bg-[#1C1C1E] border border-white/10 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-white/30"
                        >
                          <option value="secretary">Secrétaire</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button
                          onClick={() => toggleActive(u)}
                          data-testid={`user-toggle-${u.username}`}
                          title={u.active ? "Désactiver" : "Réactiver"}
                          className="p-2 bg-white/5 hover:bg-white/10 text-white rounded"
                        >
                          {u.active ? <UserX size={14} /> : <UserCheck size={14} />}
                        </button>
                        <button
                          onClick={() => deleteUser(u)}
                          data-testid={`user-delete-${u.username}`}
                          title="Supprimer"
                          className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Password reset inline */}
                {!isSelf && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.06]">
                    <KeyRound size={14} className="text-white/40 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Nouveau mot de passe (8 car. min)"
                      value={passwordDraft[u.id] || ""}
                      onChange={(e) => setPasswordDraft((p) => ({ ...p, [u.id]: e.target.value }))}
                      data-testid={`user-password-input-${u.username}`}
                      autoCapitalize="none"
                      autoComplete="new-password"
                      className="flex-1 bg-[#1C1C1E] border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-white/30"
                    />
                    <button
                      onClick={() => resetPassword(u)}
                      data-testid={`user-password-save-${u.username}`}
                      disabled={!(passwordDraft[u.id] || "").trim()}
                      className="inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 disabled:opacity-40 text-white px-3 py-1.5 rounded text-xs uppercase tracking-wider"
                    >
                      <Pencil size={11} /> Mettre à jour
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="text-white/40 text-xs leading-relaxed border-t border-white/[0.06] pt-4">
        <p className="font-semibold text-white/60 mb-1 uppercase tracking-wider">À savoir</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Le compte <code className="bg-white/5 px-1 rounded">admin</code> est protégé par la variable d'environnement du serveur — son mot de passe se change via le fichier .env (demandez au support).</li>
          <li>Les comptes <em>secrétaire</em> n'accèdent qu'au Planning (Calendrier, RDV, Création RDV, RDV Effectués) et aux Clients.</li>
          <li>Un compte désactivé reste visible mais ne peut plus se connecter.</li>
        </ul>
      </div>
    </div>
  );
}
